import { useState } from "react";
import { useSession } from "@clerk/react";
import { parseDate } from "@internationalized/date";
import { Button, toast } from "@heroui/react";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";

import StepperDesktop from "./comps/stepper-desktop";
import DetailsStep from "./comps/details-step";
import ParticipantsStep from "./comps/participants-step";
import ConfirmStep from "./comps/confirmation-step";

import { createMeet as createMeetApi } from "@/api/meets";
import { buildLocationPayload } from "@/utils/meets";
import { getCurrentDate, getPrev15MinTime, getNextAvailable15MinTimeBasedOnDuration } from "@/utils/dates";

const CreateMeetsDesktop = ({ _onResp, onCancel, validate = true, initialValues }) => {
  const { session } = useSession();

  const [activeStep, setActiveStep] = useState("details");
  const [completedSteps, setCompletedSteps] = useState([]);
  const [creating, setCreating] = useState(false);

  const [formData, setFormData] = useState(() => ({
    requestId: crypto.randomUUID(),
    recurrence: { frequency: "once", count: 4 },
    title: "",
    date: parseDate(initialValues?.date ?? getCurrentDate()),
    startTime: initialValues?.startTime ?? getPrev15MinTime(),
    endTime: initialValues?.endTime ?? getNextAvailable15MinTimeBasedOnDuration(1),
    location: null,
    description: "",
    participants: (initialValues && initialValues.participants) ?? [],
  }));

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const steps = {
    details: {
      id: "details",
      label: "Details",
      description: "Name, date and place",
      heading: "Meeting details",
      subheading: "Set the basics — you can change any of this later.",
    },
    participants: {
      id: "participants",
      label: "Participants",
      description: "Who's joining",
      heading: "Add participants",
      subheading: "Pick people from your network or create a new contact.",
    },
    confirm: {
      id: "confirm",
      label: "Confirm",
      description: "Review and create",
      heading: "Review meeting",
      subheading: "Check everything over before you send the invites.",
    },
  };

  const stepsArr = Object.values(steps);
  const activeIndex = stepsArr.findIndex((s) => s.id === activeStep);
  const current = steps[activeStep];
  const isLastStep = activeIndex === stepsArr.length - 1;

  function validateStep(stepId) {
    if (!validate) return true;

    if (stepId === "details") {
      if (!formData.title.trim()) {
        toast.danger("Title missing", { description: "Give the meeting a name so people know what it's for." });
        return false;
      }
      if (!formData.location) {
        toast.danger("Location missing", { description: "Search for an address or place to continue." });
        return false;
      }
    }

    if (stepId === "participants") {
      if (!formData.participants || formData.participants.length === 0) {
        toast.danger("No participants selected", { description: "Add at least one person to this meeting." });
        return false;
      }
    }

    return true;
  }

  const goNext = () => {
    if (!validateStep(activeStep)) return;
    setCompletedSteps((prev) => (prev.includes(activeStep) ? prev : [...prev, activeStep]));
    if (activeIndex < stepsArr.length - 1) setActiveStep(stepsArr[activeIndex + 1].id);
  };

  const goBack = () => {
    if (activeIndex === 0) {
      _onResp?.("cancel", null);
      onCancel?.();
      return;
    }
    setActiveStep(stepsArr[activeIndex - 1].id);
  };

  async function onCreateClick() {
    if (!validateStep("details") || !validateStep("participants")) return;

    const token = await session.getToken();
    const params = {
      request_id: formData.requestId,
      recurrence: formData.recurrence,
      title: formData.title,
      description: formData.description,
      meeting_date: formData.date?.toString() ?? "",
      start_time: formData.startTime,
      end_time: formData.endTime,
      members: formData.participants.map((p) => p.id),
      location: buildLocationPayload(formData.location),
    };

    setCreating(true);
    createMeetApi(params, token)
      .then((_resp) => {
        if (_resp?.data?.Result === "SUCCESS") {
          toast.success("Meeting created");
          _onResp?.("success", _resp.data);
        } else {
          toast.danger("Couldn't create meeting", { description: "The server rejected the request. Try again." });
        }
      })
      .catch((err) => {
        console.error(err);
        toast.danger("Couldn't create meeting", { description: "Check your connection and try again." });
      })
      .finally(() => setCreating(false));
  }

  const renderStep = () => {
    switch (activeStep) {
      case "details":
        return <DetailsStep formData={formData} updateFormData={updateFormData} />;
      case "participants":
        return <ParticipantsStep formData={formData} updateFormData={updateFormData} />;
      case "confirm":
        return (
          <ConfirmStep
            formData={formData}
            updateFormData={updateFormData}
            onEditParticipants={() => setActiveStep("participants")}
            onEditDate={() => setActiveStep("details")}
            onEditLocation={() => setActiveStep("details")}
            onEditTitle={() => setActiveStep("details")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden bg-surface text-foreground">
      {/* ── Rail ─────────────────────────────────────────────────────────── */}
      <aside className="w-[248px] shrink-0 flex-col justify-between border-r border-separator bg-accent-soft p-6 flex">
        <div className="flex flex-col gap-7">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-bold tracking-tight">New meeting</h2>
            <p className="text-xs leading-snug text-muted">Three quick steps and you're done.</p>
          </div>

          <StepperDesktop
            steps={stepsArr}
            activeStep={activeStep}
            completedSteps={completedSteps}
            onStepSelect={setActiveStep}
          />
        </div>

        <p className="text-xs leading-snug text-muted">
          Participants who enabled email notifications receive an invitation.
        </p>
      </aside>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-start justify-between gap-6 border-b border-separator px-6 py-5 lg:px-8">
          <div className="flex min-w-0 flex-col gap-3">
            {/* <StepperDesktop
              steps={stepsArr}
              activeStep={activeStep}
              completedSteps={completedSteps}
              onStepSelect={setActiveStep}
              orientation="horizontal"
              className="lg:hidden"
            /> */}
            <div className="flex flex-col gap-1">
              <h3 className="text-xl font-bold tracking-tight">{current.heading}</h3>
              <p className="text-sm text-muted">{current.subheading}</p>
            </div>
          </div>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-default hover:text-foreground"
            >
              <X size={18} />
            </button>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 lg:px-8">{renderStep()}</div>

        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-separator px-6 py-4 lg:px-8">
          <Button type="button" variant="outline" onClick={goBack} isDisabled={creating} className="h-10 font-bold">
            <ArrowLeft className="size-3.5" />
            {activeIndex === 0 ? "Cancel" : "Back"}
          </Button>

          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-muted sm:block">
              Step {activeIndex + 1} of {stepsArr.length}
            </span>

            {!isLastStep ? (
              <Button type="button" onClick={goNext} className="h-10 min-w-[132px] font-bold shadow-md shadow-accent/30 dark:shadow-none">
                Next
                <ArrowRight className="size-3.5" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={onCreateClick}
                isPending={creating}
                isDisabled={creating}
                className="h-10 min-w-[132px] font-bold shadow-md shadow-accent/30 dark:shadow-none"
              >
                {creating ? (
                  "Creating..."
                ) : (
                  <>
                    <Check className="size-3.5" />
                    Create meeting
                  </>
                )}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CreateMeetsDesktop;